import { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { decoupledSave } from '../../services/api';

/**
 * PipelineSettings — écran d'admin des pipelines de transactions (façon HubSpot).
 *
 * Élément reactive-json AUTONOME (fait ses propres fetch decoupled). Justifié
 * config-first : l'UI groupe des étapes PAR pipeline (le Switch YAML n'itère qu'un
 * tableau sans regroupement), et le RÉORDONNANCEMENT drag-drop (@dnd-kit) n'a pas
 * de primitive déclarative. Lecture = collections decoupled ; écriture = decoupledSave
 * (CSRF géré globalement). Bundles : node/pipeline + node/pipeline_stage.
 *
 * Couleur par défaut dérivée du type (surcharge = field_stage_color).
 */

const TYPE_META = {
  open: { label: 'Ouverte', color: '#6366f1' },
  won: { label: 'Gagnée', color: '#16a34a' },
  lost: { label: 'Perdue', color: '#e11d48' },
  nogo: { label: 'NoGo', color: '#64748b' },
};
const stageColor = (s) => (s.color && String(s.color).trim()) || TYPE_META[s.stageType]?.color || '#94a3b8';

async function fetchCollection(bundle) {
  const res = await fetch(`/decoupled-api/node/${bundle}/collection?display=decoupled`, { credentials: 'include' });
  if (!res.ok) throw new Error(`${bundle} ${res.status}`);
  const j = await res.json();
  const items = Array.isArray(j) ? j : (j.data ?? j.contents ?? []);
  return items.map((it) => ({ ...it.data, _submission: it.submission_url, _delete: it.delete_url }));
}

function StageRow({ stage, onRename, onChangeType, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const meta = TYPE_META[stage.stageType] ?? TYPE_META.open;
  return (
    <div ref={setNodeRef} style={style}
      className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 bg-white">
      <button {...attributes} {...listeners} type="button" aria-label="Réordonner"
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 px-1">⠿</button>
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: stageColor(stage) }} />
      <input defaultValue={stage.name}
        onBlur={(e) => { if (e.target.value !== stage.name) onRename(stage, e.target.value); }}
        className="flex-grow min-w-0 bg-transparent text-sm text-slate-800 outline-none focus:bg-slate-50 rounded px-1 py-0.5"
        aria-label="Nom de l'étape" />
      <select value={stage.stageType ?? 'open'}
        onChange={(e) => onChangeType(stage, e.target.value)}
        aria-label={`Statut de l'étape ${stage.name}`}
        className="select w-28 h-7 text-xs font-semibold shrink-0"
        style={{ backgroundColor: meta.color + '1a', color: meta.color, borderColor: meta.color + '40' }}>
        {Object.entries(TYPE_META).map(([value, m]) => (
          <option key={value} value={value}>{m.label}</option>
        ))}
      </select>
      {stage.probability != null && (
        <span className="text-xs text-slate-400 tabular-nums shrink-0 w-10 text-right">
          {Math.round(parseFloat(stage.probability) * 100)}%</span>
      )}
      <button type="button" onClick={() => onDelete(stage)} title="Supprimer l'étape"
        className="text-slate-300 hover:text-rose-600 px-1 shrink-0">✕</button>
    </div>
  );
}

function PipelineCard({ pipeline, stages, onReorder, onRename, onRenamePipeline, onChangeType, onAddStage, onDeleteStage }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIx = stages.findIndex((s) => s.id === active.id);
    const newIx = stages.findIndex((s) => s.id === over.id);
    onReorder(pipeline.id, arrayMove(stages, oldIx, newIx));
  };
  // Intitulé éditable en place : on ne persiste qu'un nom non vide et réellement
  // changé, sinon on rétablit l'affiché (Entrée = valider, Échap = annuler).
  const commitName = (el) => {
    const next = el.value.trim();
    if (!next || next === pipeline.name) { el.value = pipeline.name; return; }
    onRenamePipeline(pipeline, next);
  };
  return (
    <div className="card">
      <div className="card-body space-y-3">
        <h2 className="flex items-center gap-2">
          <input defaultValue={pipeline.name}
            onBlur={(e) => commitName(e.target)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
              else if (e.key === 'Escape') { e.target.value = pipeline.name; e.target.blur(); }
            }}
            aria-label="Nom du pipeline"
            className="input-inline h-section w-auto flex-grow min-w-0" />
          <span className="badge badge-primary badge-sm shrink-0">{stages.length} étapes</span>
        </h2>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {stages.map((s) => (
                <StageRow key={s.id} stage={s} onRename={onRename} onChangeType={onChangeType}
                  onDelete={onDeleteStage} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <button type="button" onClick={() => onAddStage(pipeline)} className="btn btn-secondary btn-sm">
          + Ajouter une étape
        </button>
      </div>
    </div>
  );
}

export function PipelineSettings() {
  const [pipelines, setPipelines] = useState([]);
  const [stagesByPipe, setStagesByPipe] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const busy = useRef(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [pipes, stages] = await Promise.all([fetchCollection('pipeline'), fetchCollection('pipeline_stage')]);
      pipes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const grp = {};
      for (const s of stages) {
        (grp[s.pipelineId] ??= []).push(s);
      }
      for (const k of Object.keys(grp)) grp[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setPipelines(pipes); setStagesByPipe(grp);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Persiste le nouvel ordre des étapes réordonnées (order = index).
  const onReorder = async (pipeId, ordered) => {
    setStagesByPipe((prev) => ({ ...prev, [pipeId]: ordered }));
    const changed = ordered.filter((s, i) => s.order !== i);
    for (let i = 0; i < ordered.length; i++) {
      if (ordered[i].order !== i) {
        try { await decoupledSave('node', 'pipeline_stage', { order: i }, ordered[i].id); } catch (e) { setError(String(e)); }
      }
    }
    if (changed.length) load();
  };

  // Renommage du pipeline : optimiste (le titre reste cohérent avec les libellés
  // dérivés), rechargement si le back refuse pour que l'écran ne mente pas.
  const onRenamePipeline = async (pipeline, name) => {
    setPipelines((prev) => prev.map((p) => (p.id === pipeline.id ? { ...p, name } : p)));
    try { await decoupledSave('node', 'pipeline', { name }, pipeline.id); }
    catch (e) { setError(String(e)); load(); }
  };

  const onRename = async (stage, name) => {
    try { await decoupledSave('node', 'pipeline_stage', { name }, stage.id); } catch (e) { setError(String(e)); }
  };

  // Statut de l'étape (Ouverte / Gagnée / Perdue / NoGo) : mise à jour optimiste puis
  // persistance ; en cas d'échec on recharge pour que l'écran ne mente pas.
  const onChangeType = async (stage, stageType) => {
    setStagesByPipe((prev) => ({
      ...prev,
      [stage.pipelineId]: (prev[stage.pipelineId] ?? []).map((s) => (s.id === stage.id ? { ...s, stageType } : s)),
    }));
    try { await decoupledSave('node', 'pipeline_stage', { stageType }, stage.id); }
    catch (e) { setError(String(e)); load(); }
  };
  const onAddStage = async (pipeline) => {
    if (busy.current) return; busy.current = true;
    const order = (stagesByPipe[pipeline.id]?.length) ?? 0;
    try {
      await decoupledSave('node', 'pipeline_stage', { name: 'Nouvelle étape', pipelineId: pipeline.id, order, stageType: 'open' }, null);
      await load();
    } catch (e) { setError(String(e)); } finally { busy.current = false; }
  };
  const onAddPipeline = async () => {
    if (busy.current) return; busy.current = true;
    try {
      await decoupledSave('node', 'pipeline', { name: 'Nouveau pipeline', order: pipelines.length }, null);
      await load();
    } catch (e) { setError(String(e)); } finally { busy.current = false; }
  };
  const onDeleteStage = async (stage) => {
    if (!stage._delete || !window.confirm(`Supprimer l'étape « ${stage.name} » ?`)) return;
    try { await fetch(stage._delete, { method: 'DELETE', credentials: 'include' }); await load(); } catch (e) { setError(String(e)); }
  };

  if (loading) return <p className="text-slate-500 p-4">Chargement des pipelines…</p>;

  return (
    <div className="space-y-4">
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{pipelines.length} pipeline(s) — glissez-déposez ⠿ pour réordonner les étapes ; intitulé du pipeline, nom et statut d'étape éditables en place.</p>
        <button type="button" onClick={onAddPipeline} className="btn btn-primary shrink-0">+ Nouveau pipeline</button>
      </div>
      {pipelines.map((p) => (
        <PipelineCard key={p.id} pipeline={p} stages={stagesByPipe[p.id] ?? []}
          onReorder={onReorder} onRename={onRename} onRenamePipeline={onRenamePipeline}
          onChangeType={onChangeType}
          onAddStage={onAddStage} onDeleteStage={onDeleteStage} />
      ))}
    </div>
  );
}

export default PipelineSettings;
